import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { ListStatus } from '@/components/list/ListStatus';
import { CollapsiblePanel } from '@/components/CollapsiblePanel';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePagedList } from '@/hooks/usePagedList';
import { formatMinutes } from '@/lib/duration';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { TIME_SHEET_SUMMARIES } from '@/models/entities/TimeSheetSummaryModel';
import { TIME_SHEET_STATUS, TIME_SHEET_TYPE } from '@/models/references/references';
import type { ResolvablePath } from '@/models/resolve';
import { findReferenceId, useReferenceOptions } from '@/stores/useReferenceOptions';
import { BREAKDOWN_PAGE_SIZE, breakdownRequest, columnHeight, hourTicks } from './dashboardData';

/** Nom des collaborateurs */
const RESOLVABLES: ResolvablePath[] = [['member', 'user']];

/**
 * Couleurs d'état (statut, pas de série) : validée vert, en attente orange hachuré. Le hachurage double la couleur
 * (orange et vert se confondent presque en protanopie, ΔE 7 : skill dataviz) ; heures et statut sont aussi écrits.
 */
const VALIDATED_FILL = 'bg-green-600';
const PENDING_FILL =
  'bg-amber-500 bg-[repeating-linear-gradient(45deg,rgb(255_255_255/0.45)_0_3px,transparent_3px_7px)]';

/**
 * Répartition par collaborateur (Tableau de bord) : une colonne par collaborateur de la période (sa feuille soumise,
 * absences exclues), par heures décroissantes, 10 par page (‹ ›), toutes à la même échelle des heures (graduations
 * rondes au-dessus du plus gros total de la période, lignes de repère).
 * Verte validée, orange hachurée en attente ; heures au-dessus, NOM Prénom en dessous, statut au survol ; une colonne
 * ouvre la feuille (sheetPath : dans la Validation par défaut). Aussi dans le récapitulatif d'une période clôturée de
 * l'Historique (closed) : tout y est validé, la légende ne garde que « Validée ».
 */
export function CollaboratorBreakdown({
  periodId,
  teamIds,
  closed = false,
  sheetPath = (sheetId) => `/validation/${sheetId}`,
}: {
  periodId: number;
  /** Équipes du manager (null : le Dirigeant, toute la société) */
  teamIds: number[] | null;
  /** Période clôturée : feuilles toutes validées, pas de légende « En attente » */
  closed?: boolean;
  /** Page d'une feuille, ouverte au clic d'une colonne */
  sheetPath?: (sheetId: string) => string;
}) {
  const [page, setPage] = useState(1);
  const [maxMinutes, setMaxMinutes] = useState(0);
  const statuses = useReferenceOptions('timeSheetStatuses');
  const types = useReferenceOptions('timeSheetTypes');
  const request = useMemo(
    () =>
      breakdownRequest(
        periodId,
        teamIds,
        findReferenceId(statuses, TIME_SHEET_STATUS.DRAFT),
        findReferenceId(types, TIME_SHEET_TYPE.NORMAL),
        page,
      ),
    [periodId, teamIds, statuses, types, page],
  );
  const {
    models: sheets,
    totalItems,
    loading,
    error,
  } = usePagedList(TimeSheetModel, request, 0, {
    resolvables: RESOLVABLES,
    summary: TIME_SHEET_SUMMARIES,
  });
  // Échelle commune à toutes les pages : le premier total de la page 1 (tri décroissant)
  const pageMax = Math.max(0, ...sheets.map((sheet) => sheet.totalMinutes));
  if (page === 1 && !loading && sheets.length && pageMax !== maxMinutes) {
    setMaxMinutes(pageMax);
  }
  // Échelle des heures : graduations rondes jusqu'au-dessus du plus gros total, qui en donnent le haut
  const ticks = hourTicks(Math.max(maxMinutes, pageMax));
  const scale = ticks.at(-1)!;
  const first = (page - 1) * BREAKDOWN_PAGE_SIZE + 1;
  const last = Math.min(page * BREAKDOWN_PAGE_SIZE, totalItems);

  return (
    <CollapsiblePanel
      title="Répartition par collaborateur"
      count={loading && !sheets.length ? undefined : totalItems}
      actions={
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
          {!closed && (
            <span className="flex items-center gap-1.5">
              <span className={clsx('w-3 h-3 rounded-sm', PENDING_FILL)} aria-hidden />
              En attente
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span className={clsx('w-3 h-3 rounded-sm', VALIDATED_FILL)} aria-hidden />
            Validée
          </span>
          {totalItems > BREAKDOWN_PAGE_SIZE && (
            <span className="flex items-center gap-1" data-testid="breakdown-pager">
              <button
                type="button"
                aria-label="Collaborateurs précédents"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="tabular-nums">
                {first}–{last} sur {totalItems}
              </span>
              <button
                type="button"
                aria-label="Collaborateurs suivants"
                disabled={last >= totalItems}
                onClick={() => setPage(page + 1)}
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </span>
          )}
        </div>
      }
    >
      <div className="p-6">
        {sheets.length ? (
          <>
            <div className="flex">
              {/* Échelle des heures (axe vertical) */}
              <div className="relative w-12 h-72 shrink-0" aria-hidden data-testid="breakdown-scale">
                {ticks.map((tick) => (
                  <span
                    key={tick}
                    className="absolute right-2 translate-y-1/2 text-xs text-slate-400 tabular-nums"
                    style={{ bottom: `${columnHeight(tick, scale)}%` }}
                  >
                    {formatMinutes(tick)}
                  </span>
                ))}
              </div>
              <div className="relative flex-1 h-72 border-b border-slate-300">
                {/* Lignes de repère, discrètes */}
                {ticks.slice(1).map((tick) => (
                  <div
                    key={tick}
                    className="absolute inset-x-0 border-t border-slate-100"
                    style={{ bottom: `${columnHeight(tick, scale)}%` }}
                    aria-hidden
                  />
                ))}
                <ol
                  className={clsx(
                    'relative grid grid-cols-10 gap-2 h-full transition-opacity',
                    loading && 'opacity-60',
                  )}
                  aria-busy={loading}
                >
                  {sheets.map((sheet) => {
                    const height = columnHeight(sheet.totalMinutes, scale);
                    return (
                      <li key={sheet.id} className="relative h-full" data-testid="breakdown-column">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={sheetPath(String(sheet.id))}
                              aria-label={`${sheet.fullName} : ${formatMinutes(sheet.totalMinutes)}, ${sheet.timeSheetStatusLabel}`}
                              className="group absolute inset-0 flex justify-center rounded-md focus-visible:outline-2 focus-visible:outline-blue-600"
                            >
                              <span
                                className="absolute text-xs font-semibold text-slate-700 tabular-nums mb-1"
                                style={{ bottom: `${height}%` }}
                              >
                                {formatMinutes(sheet.totalMinutes)}
                              </span>
                              <span
                                className={clsx(
                                  'absolute bottom-0 w-full max-w-10 rounded-t min-h-0.5 group-hover:opacity-80 transition-opacity',
                                  sheet.isPending ? PENDING_FILL : VALIDATED_FILL,
                                )}
                                style={{ height: `${height}%` }}
                                data-status={sheet.isPending ? 'pending' : 'validated'}
                              />
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sheet.fullName} · {formatMinutes(sheet.totalMinutes)} · {sheet.timeSheetStatusLabel}
                          </TooltipContent>
                        </Tooltip>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
            <ol className="grid grid-cols-10 gap-2 mt-2 ml-12" aria-hidden>
              {sheets.map((sheet) => (
                <li key={sheet.id} className="text-center text-xs text-slate-500 leading-tight min-w-0">
                  <span className="block truncate font-medium text-slate-700">{sheet.lastName}</span>
                  <span className="block truncate">{sheet.firstName}</span>
                </li>
              ))}
            </ol>
          </>
        ) : (
          <ListStatus
            loading={!request || (loading && !sheets.length)}
            error={error}
            empty={!loading && 'Aucune feuille soumise pour cette période.'}
          />
        )}
      </div>
    </CollapsiblePanel>
  );
}
